/**
 * Haptic feedback utility for iOS devices using the Vibration API.
 * Falls back gracefully on unsupported devices.
 */

type HapticIntensity = 'light' | 'medium' | 'heavy';

const VIBRATION_PATTERNS: Record<HapticIntensity, number | number[]> = {
  light: 10,
  medium: 20,
  heavy: 30,
};

/**
 * Check if haptic feedback is supported on the current device.
 */
export function isHapticSupported(): boolean {
  return 'vibrate' in navigator;
}

/**
 * Trigger haptic feedback with the specified intensity.
 * Falls back silently on unsupported devices.
 */
export function triggerHaptic(intensity: HapticIntensity = 'medium'): void {
  if (!isHapticSupported()) return;

  try {
    const pattern = VIBRATION_PATTERNS[intensity];
    navigator.vibrate(pattern);
  } catch {
    // Silently fail if vibration is not available
  }
}

/**
 * Trigger light haptic feedback (used for threshold hints).
 */
export function hapticLight(): void {
  triggerHaptic('light');
}

/**
 * Trigger medium haptic feedback (used for action confirmations).
 */
export function hapticMedium(): void {
  triggerHaptic('medium');
}

/**
 * Trigger heavy haptic feedback (used for major actions).
 */
export function hapticHeavy(): void {
  triggerHaptic('heavy');
}
